import React, {useEffect, useState} from 'react';
import createAxiosInstance from '../../support/AxiosSupport';
import authService from '../../service/AuthService';
import logger from '../../utils/logger';
import {ChevronDownIcon, ChevronUpIcon, CookieIcon, FileTextIcon, XIcon} from '../icons';

/**
 * Cookie-баннер согласия при первом посещении web-приложения (CTHS-226, эпик CTHS-165
 * «Privacy Center & Account Lifecycle»). Первый видимый compliance-элемент (152-ФЗ):
 * при первом визите фиксирует cookie-согласие в localStorage и — при наличии JWT —
 * синхронизирует `analytics_consent` с бэкендом (`PATCH /api/privacy/consent`).
 *
 * Глобальный overlay рядом с {@link '../UpgradePrompt'}; на xs/sm — bottom sheet,
 * на md+ — центрированная карточка внизу экрана. Все цвета — токены styleguide,
 * иконки — Lucide (C-6.1, C-6.2), тач-зоны ≥ 44px (C-6.4).
 *
 * Graceful degradation (C-2.7): недоступный localStorage (приватный режим), недоступные
 * `PATCH /api/privacy/consent` (ждёт CTHS-223) и `GET /api/policy-documents/...` (ждёт CTHS-221)
 * не ломают баннер — ошибка логируется, выбор сохраняется локально, ссылка на политику скрывается.
 */

const STORAGE_KEY = 'cookie_consent';
const CONSENT_VERSION = '1';
const POLICY_PATH = '/policy-documents/COOKIE_POLICY/active';
const CONSENT_PATH = '/privacy/consent';

interface PolicyDocument {
    contentUrl?: string | null;
    content?: string | null;
}

/** Есть ли что показать по ссылке «Политика cookies» (AC-6): ссылка ИЛИ inline-контент. */
const hasPolicy = (p: PolicyDocument | null): boolean =>
    !!p && (!!p.contentUrl || !!p.content);

const CookieBanner: React.FC = () => {
    // Показ только при первом визите (нет ключа в localStorage). Приватный режим, где
    // localStorage недоступен, не должен крашить рендер — в этом случае показываем баннер.
    const [visible, setVisible] = useState<boolean>(() => {
        try {
            return !localStorage.getItem(STORAGE_KEY);
        } catch {
            return true;
        }
    });
    const [showSettings, setShowSettings] = useState(false);
    const [analytics, setAnalytics] = useState(false);
    const [functional, setFunctional] = useState(false);
    const [policy, setPolicy] = useState<PolicyDocument | null>(null);
    const [showPolicyInline, setShowPolicyInline] = useState(false);

    // AC-6: публичный документ политики cookies. Запрос не блокирует рендер баннера;
    // при ошибке/404 ссылка просто не появляется (graceful degradation).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const api = await createAxiosInstance();
                const res = await api.get<PolicyDocument>(POLICY_PATH);
                if (!cancelled) {
                    setPolicy(res.data);
                }
            } catch (e) {
                logger.warn('CookieBanner: cookie policy document unavailable', {error: String(e)});
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // AC-5/AC-9: синхронизация выбора analytics с бэкендом только для авторизованного
    // пользователя. Аноним — только localStorage, без запроса. Ошибка не показывается
    // пользователю (negative case C-2.7); код готов к приходу backend-метода из CTHS-223.
    const syncAnalytics = async (analyticsConsent: boolean): Promise<void> => {
        if (!authService.isAuthenticated()) {
            return;
        }
        try {
            const api = await createAxiosInstance();
            await api.patch(CONSENT_PATH, {analyticsConsent});
        } catch (e) {
            logger.warn('CookieBanner: analytics consent sync failed', {error: String(e)});
        }
    };

    // AC-3/AC-4: фиксируем выбор в localStorage, скрываем баннер и синхронизируем с бэкендом.
    const persist = (analyticsChoice: boolean, functionalChoice: boolean): void => {
        const payload = {
            version: CONSENT_VERSION,
            date: new Date().toISOString(),
            necessary: true,
            analytics: analyticsChoice,
            functional: functionalChoice,
        };
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
        } catch (e) {
            // Приватный режим: запись невозможна — баннер всё равно прячем на эту сессию.
            logger.warn('CookieBanner: localStorage unavailable, consent not persisted', {error: String(e)});
        }
        setVisible(false);
        void syncAnalytics(analyticsChoice);
    };

    if (!visible) {
        return null;
    }

    const acceptAll = () => persist(true, true);
    const onlyNecessary = () => persist(false, false);
    const saveSettings = () => persist(analytics, functional);

    return (
        <>
            <div
                role="dialog"
                aria-label="Согласие на использование cookies"
                aria-modal="false"
                data-testid="cookie-banner"
                className="fixed z-[1050] bottom-0 inset-x-0 rounded-t-2xl
                           md:bottom-4 md:inset-x-4 md:mx-auto
                           md:max-w-2xl md:rounded-2xl
                           border border-ink-100 dark:border-ink-700
                           bg-white dark:bg-ink-800 shadow-card-hover p-4 sm:p-5 animate-fade-in"
            >
                <div className="flex items-start gap-3">
                    <div className="shrink-0 w-9 h-9 rounded-xl bg-brand-50 dark:bg-brand-900/40
                                    flex items-center justify-center text-brand-600 dark:text-brand-400">
                        <CookieIcon className="w-5 h-5"/>
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="font-semibold text-gray-900 dark:text-gray-100">
                            Мы используем cookies
                        </div>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                            Необходимые cookies обеспечивают работу сайта. Аналитические и функциональные —
                            только с вашего согласия (152-ФЗ).
                            {hasPolicy(policy) && (
                                policy?.contentUrl ? (
                                    <a
                                        href={policy.contentUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="cookie-policy-link"
                                        className="ml-1 text-brand-600 dark:text-brand-400 underline
                                                   hover:text-brand-700 dark:hover:text-brand-300"
                                    >
                                        Политика cookies
                                    </a>
                                ) : (
                                    <button
                                        type="button"
                                        data-testid="cookie-policy-link"
                                        onClick={() => setShowPolicyInline(true)}
                                        className="ml-1 text-brand-600 dark:text-brand-400 underline
                                                   hover:text-brand-700 dark:hover:text-brand-300"
                                    >
                                        Политика cookies
                                    </button>
                                )
                            )}
                        </p>

                        {showSettings && (
                            <div
                                data-testid="cookie-settings-panel"
                                className="mt-4 space-y-3 border-t border-ink-100 dark:border-ink-700 pt-4"
                            >
                                <label className="flex items-start gap-3 opacity-70 cursor-not-allowed">
                                    <input
                                        type="checkbox"
                                        checked
                                        disabled
                                        data-testid="cookie-necessary"
                                        className="mt-1 accent-brand-600 w-5 h-5 shrink-0"
                                    />
                                    <span>
                                        <span className="text-gray-900 dark:text-gray-100">Обязательные</span>
                                        <span className="block text-sm text-gray-500 dark:text-gray-400">
                                            Сессия и авторизация. Всегда включены — необходимы для работы сайта.
                                        </span>
                                    </span>
                                </label>
                                <label className="flex items-start gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={analytics}
                                        onChange={e => setAnalytics(e.target.checked)}
                                        data-testid="cookie-analytics"
                                        className="mt-1 accent-brand-600 w-5 h-5 shrink-0"
                                    />
                                    <span>
                                        <span className="text-gray-900 dark:text-gray-100">Аналитические</span>
                                        <span className="block text-sm text-gray-500 dark:text-gray-400">
                                            Помогают понять, как используется приложение.
                                        </span>
                                    </span>
                                </label>
                                <label className="flex items-start gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={functional}
                                        onChange={e => setFunctional(e.target.checked)}
                                        data-testid="cookie-functional"
                                        className="mt-1 accent-brand-600 w-5 h-5 shrink-0"
                                    />
                                    <span>
                                        <span className="text-gray-900 dark:text-gray-100">Функциональные</span>
                                        <span className="block text-sm text-gray-500 dark:text-gray-400">
                                            Запоминают предпочтения для удобства интерфейса.
                                        </span>
                                    </span>
                                </label>
                            </div>
                        )}

                        <div className="mt-4 flex flex-col sm:flex-row sm:flex-wrap gap-2">
                            {showSettings ? (
                                <button
                                    type="button"
                                    data-testid="cookie-save"
                                    onClick={saveSettings}
                                    className="inline-flex items-center justify-center min-h-[44px] px-4
                                               rounded-xl bg-brand-600 text-white text-sm font-medium
                                               hover:bg-brand-700 transition-colors"
                                >
                                    Сохранить
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    data-testid="cookie-accept-all"
                                    onClick={acceptAll}
                                    className="inline-flex items-center justify-center min-h-[44px] px-4
                                               rounded-xl bg-brand-600 text-white text-sm font-medium
                                               hover:bg-brand-700 transition-colors"
                                >
                                    Принять все
                                </button>
                            )}
                            <button
                                type="button"
                                data-testid="cookie-only-necessary"
                                onClick={onlyNecessary}
                                className="inline-flex items-center justify-center min-h-[44px] px-4
                                           rounded-xl border border-ink-100 dark:border-ink-700
                                           text-gray-700 dark:text-gray-200 text-sm font-medium
                                           hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                            >
                                Только необходимые
                            </button>
                            {!showSettings && (
                                <button
                                    type="button"
                                    data-testid="cookie-customize"
                                    onClick={() => setShowSettings(true)}
                                    className="inline-flex items-center justify-center gap-1.5 min-h-[44px] px-4
                                               rounded-xl border border-ink-100 dark:border-ink-700
                                               text-gray-700 dark:text-gray-200 text-sm font-medium
                                               hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                                >
                                    Настроить
                                    <ChevronDownIcon className="w-4 h-4"/>
                                </button>
                            )}
                            {showSettings && (
                                <button
                                    type="button"
                                    data-testid="cookie-collapse"
                                    onClick={() => setShowSettings(false)}
                                    className="inline-flex items-center justify-center gap-1.5 min-h-[44px] px-4
                                               rounded-xl text-gray-500 dark:text-gray-400 text-sm font-medium
                                               hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                                >
                                    Свернуть
                                    <ChevronUpIcon className="w-4 h-4"/>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {showPolicyInline && policy?.content && (
                <div
                    role="dialog"
                    aria-label="Политика cookies"
                    aria-modal="true"
                    data-testid="cookie-policy-inline"
                    className="fw-overlay fixed inset-0 z-[1100] flex items-center justify-center p-4
                               bg-black/50 animate-fade-in"
                    onClick={() => setShowPolicyInline(false)}
                >
                    <div
                        className="relative w-full max-w-2xl max-h-[80vh] overflow-y-auto
                                   rounded-2xl bg-white dark:bg-ink-800 shadow-card-hover p-6"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between gap-3 mb-3">
                            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
                                <FileTextIcon className="w-5 h-5 text-brand-600 dark:text-brand-400"/>
                                Политика cookies
                            </h2>
                            <button
                                type="button"
                                aria-label="Закрыть"
                                data-testid="cookie-policy-close"
                                onClick={() => setShowPolicyInline(false)}
                                className="shrink-0 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center
                                           rounded-xl text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                            >
                                <XIcon className="w-5 h-5"/>
                            </button>
                        </div>
                        <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                            {policy.content}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CookieBanner;
